/**
 * Sanitizes SPA route paths before they are pushed to the marketing data layer.
 *
 * Business rules (Feature 679 / GA4):
 * - Query strings and URL fragments are stripped entirely so session IDs, edit
 *   params, and Stripe recovery tokens never reach Google Analytics.
 * - Dynamic path segments that may carry PII (card view tokens, numeric record
 *   IDs) are replaced with stable placeholders so GA4 reports stay aggregatable.
 * - Known static public routes pass through unchanged.
 * - `/card/preview` is a static marketing preview route and must not be collapsed
 *   into `/card/:token`.
 */

/** Placeholder for recipient card view tokens (`/card/:viewToken`). */
export const SANITIZED_CARD_TOKEN_SEGMENT = '/card/:token';

/** Placeholder for user support-ticket thread IDs. */
export const SANITIZED_CONTACT_US_THREAD_SEGMENT = '/contact-us/:id';

/** Placeholder for admin support-ticket detail IDs. */
export const SANITIZED_ADMIN_SUPPORT_TICKET_SEGMENT = '/admin/support-tickets/:id';

/**
 * Strips query string and hash from a raw path or full location string.
 */
export function stripQueryAndFragment(rawPath: string): string {
  const withoutQuery = rawPath.split('?')[0] ?? rawPath;
  return withoutQuery.split('#')[0] ?? withoutQuery;
}

/**
 * Replaces dynamic/PII-bearing pathname segments with stable GA4-safe placeholders.
 *
 * Uses an allowlist-style pattern table keyed to the app's route table so new
 * PII-bearing routes must be added explicitly rather than leaking through.
 */
export function sanitizePathname(pathname: string): string {
  if (!pathname || pathname === '/') {
    return '/';
  }

  // Static card preview — must not match the token pattern below.
  if (pathname === '/card/preview') {
    return pathname;
  }

  if (pathname.startsWith('/card/')) {
    return SANITIZED_CARD_TOKEN_SEGMENT;
  }

  if (/^\/contact-us\/\d+$/.test(pathname)) {
    return SANITIZED_CONTACT_US_THREAD_SEGMENT;
  }

  if (/^\/admin\/support-tickets\/\d+$/.test(pathname)) {
    return SANITIZED_ADMIN_SUPPORT_TICKET_SEGMENT;
  }

  return pathname;
}

/**
 * Sanitizes a raw React Router path (pathname + optional query) for GA4 page_view.
 * Returns both `page_path` and `page_location` (origin + sanitized path) so GTM
 * can override GA4's auto-collected full URL.
 */
export function sanitizePagePath(rawPath: string): { page_path: string; page_location: string } {
  const pathname = stripQueryAndFragment(rawPath);
  const page_path = sanitizePathname(pathname);

  const page_location =
    typeof window !== 'undefined' && window.location?.origin
      ? `${window.location.origin}${page_path}`
      : page_path;

  return { page_path, page_location };
}
